/**
 * What the App info panel reads (DEV-1021).
 *
 * All of it is Web API or `/version.json`, so it works on any tenant domain
 * with nothing to configure. The point is supportability: when a grower says
 * "it isn't working", this is the screen that answers whether they are on the
 * current build, whether the worker is running, whether the device is full,
 * and whether they ever granted the camera.
 */

/** `deploy-helpers.sh: generate_version_json` writes this next to index.html. */
export interface BuildVersion {
  module: string;
  sha: string;
  shaShort: string;
  branch: string;
  builtAt: string;
  deployer: string;
  recentCommits?: string[];
}

/**
 * The running build's provenance.
 *
 * Deliberately NOT a hand-maintained constant. wms-mobile keeps its version in
 * three places (`sw.js` CACHE_NAME, `config.js` VERSION, `main.js`
 * CURRENT_VERSION) and two of them currently disagree, so its boot-time
 * cache-bust compares against a version two releases old. A file the deploy
 * writes cannot drift from the deploy.
 *
 * `cache: "no-store"` because a cached answer here would report the build the
 * person is trying to get away from. deploy.sh already uploads it no-cache;
 * this covers the service worker and any proxy in between.
 */
export async function fetchBuildVersion(): Promise<BuildVersion | null> {
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as BuildVersion;
  } catch {
    return null;
  }
}

/** "8 Sep 2026, 14:54" — the build date is what a person can actually compare. */
export function formatBuiltAt(builtAt: string, locale?: string): string {
  const date = new Date(builtAt);
  if (Number.isNaN(date.getTime())) return builtAt;
  return date.toLocaleString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export type WorkerState = "unsupported" | "none" | "installing" | "waiting" | "active";

export async function serviceWorkerState(): Promise<WorkerState> {
  if (!("serviceWorker" in navigator)) return "unsupported";
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return "none";
    if (reg.waiting) return "waiting";
    if (reg.installing) return "installing";
    if (reg.active) return "active";
    return "none";
  } catch {
    return "none";
  }
}

export interface StorageUsage {
  usage: number;
  quota: number;
  /** 0–100, clamped. `null` when the quota is unknown, so the bar can hide. */
  percent: number | null;
}

export async function storageUsage(): Promise<StorageUsage | null> {
  if (!navigator.storage?.estimate) return null;
  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return { usage, quota, percent: quota > 0 ? Math.min(100, (usage / quota) * 100) : null };
  } catch {
    return null;
  }
}

/** 1024-based, one decimal — "3.4 MB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** i;
  return `${i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

export type PermissionKey = "camera" | "geolocation" | "notifications" | "persistentStorage";
export type PermissionValue = "granted" | "denied" | "prompt" | "unsupported";

/**
 * The four permissions a field app actually depends on. Kept short on purpose:
 * a list of nine, most of them "Unsupported" on Safari, reads as noise rather
 * than a diagnostic.
 *
 * "Unsupported" is a first-class answer, not a blank. Most of these throw on
 * Safari, and a dash there is indistinguishable from a bug in this panel.
 */
export async function permissionStates(): Promise<Record<PermissionKey, PermissionValue>> {
  const [camera, geolocation, notifications, persistentStorage] = await Promise.all([
    queryPermission("camera"),
    queryPermission("geolocation"),
    notificationPermission(),
    persistedStorage(),
  ]);
  return { camera, geolocation, notifications, persistentStorage };
}

async function queryPermission(name: string): Promise<PermissionValue> {
  if (!navigator.permissions?.query) return "unsupported";
  try {
    const status = await navigator.permissions.query({ name } as unknown as PermissionDescriptor);
    return status.state as PermissionValue;
  } catch {
    // Safari throws for most names rather than reporting them unsupported.
    return "unsupported";
  }
}

function notificationPermission(): PermissionValue {
  if (typeof Notification === "undefined") return "unsupported";
  const value = Notification.permission;
  return value === "default" ? "prompt" : (value as PermissionValue);
}

async function persistedStorage(): Promise<PermissionValue> {
  if (!navigator.storage?.persisted) return "unsupported";
  try {
    return (await navigator.storage.persisted()) ? "granted" : "prompt";
  } catch {
    return "unsupported";
  }
}

/**
 * Turn a permission ON (DEV-1021).
 *
 * Devin: "what permissions do we have, and give them the ability to turn on
 * additional permissions?"
 *
 * Reading a permission is a query; GRANTING one is not something a page can
 * do. Only the browser can, and only in response to a real user gesture, via
 * the API that needs it — there is no `permissions.request()`. So each of
 * these calls the thing itself and immediately lets go:
 *
 *   camera       getUserMedia, then stop every track. Asking is the point;
 *                the stream is not, and leaving it open lights the recording
 *                indicator on a screen that is only reporting status.
 *   geolocation  a single position request.
 *   notifications`Notification.requestPermission()`.
 *   storage      `navigator.storage.persist()` — asks the browser not to evict
 *                the offline cache under pressure.
 *
 * A "denied" answer is FINAL: once refused, the browser will not prompt again
 * from script, and the person has to change it in site settings. The panel has
 * to say that rather than offer a button that silently does nothing.
 */
export async function requestPermission(key: PermissionKey): Promise<PermissionValue> {
  try {
    switch (key) {
      case "camera": {
        if (!navigator.mediaDevices?.getUserMedia) return "unsupported";
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((t) => t.stop());
        break;
      }
      case "geolocation": {
        if (!navigator.geolocation) return "unsupported";
        await new Promise<void>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(() => resolve(), reject, { timeout: 10_000 });
        });
        break;
      }
      case "notifications": {
        if (typeof Notification === "undefined") return "unsupported";
        await Notification.requestPermission();
        break;
      }
      case "persistentStorage": {
        if (!navigator.storage?.persist) return "unsupported";
        await navigator.storage.persist();
        break;
      }
    }
  } catch {
    // Refused, dismissed, or unavailable. Re-reading below tells the truth
    // either way, so there is nothing useful to do with the error itself.
  }
  return (await permissionStates())[key];
}

/**
 * Is a "Turn on" button worth showing?
 *
 * Only for "prompt". Granted needs nothing; denied cannot be reopened from
 * script; unsupported has nothing to ask. Offering a button in those states
 * teaches people the button is broken.
 */
export function canRequest(value: PermissionValue): boolean {
  return value === "prompt";
}

export interface ResolvedModule {
  /** "@metaship/boer" */
  name: string;
  /** The bundle filename actually in the import map — "spa.Cxbnf2gw.js". */
  bundle: string;
  /** Whether the page has actually fetched it this session. */
  loaded: boolean;
}

/**
 * WHICH BUNDLE is this shell running? (DEV-1021)
 *
 * Added after a real incident on 2026-09-09. A frontend deploy published a new
 * boer bundle and updated `metaship.ai/importmap-prod.json`, but a tenant app
 * resolves its import map from `GET /tenant-apps/resolve`, whose response was
 * cached. For ~25 minutes theboer.co.za served the PREVIOUS bundle while
 * everything about the app looked healthy — the shell's own version was
 * current, the worker was active, storage was fine. It took diffing bundle
 * hashes by hand to see it.
 *
 * That is the failure an app-level version number cannot catch: the SHELL was
 * up to date and the MODULE was not. So the panel reports the module bundles
 * too, read from the live import map — the same thing SystemJS resolves
 * against, so it cannot disagree with what is actually running.
 *
 * `loaded` separates "the map says this" from "the page fetched this", which
 * is the difference between a stale map and a stale cache.
 */
export function resolvedModules(): ResolvedModule[] {
  try {
    const tag = document.querySelector<HTMLScriptElement>('script[type="systemjs-importmap"], script[type="importmap"]');
    if (!tag?.textContent) return [];
    const imports = (JSON.parse(tag.textContent) as { imports?: Record<string, string> }).imports ?? {};
    const fetched = new Set(
      typeof performance !== "undefined"
        ? performance.getEntriesByType("resource").map((e) => e.name)
        : [],
    );
    return Object.entries(imports)
      // Metaship modules only — the shared CDN libs are noise on this screen.
      .filter(([name]) => name.startsWith("@metaship/") || name === "loglive")
      .map(([name, url]) => ({
        name,
        bundle: url.split("/").pop() || url,
        loaded: fetched.has(url),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    return [];
  }
}
